import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, FileDown, ArrowUpRight } from 'lucide-react';
import { api } from '../../lib/api';
import { KINDS, KIND_ORDER, HERO_PLASMA } from '../../lib/u99';
import { Reveal, Eyebrow } from '../../components/Reveal';
import { PageHead } from '../../components/u99/PageHead';
import { Label } from '../../components/u99/Label';
import BookFeature from '../../components/u99/BookFeature';

export const DocumentCard = ({ d, i }) => (
  <Reveal delay={Math.min(i, 8) * 0.06}>
    <Link to={`/u99/documents/${d.slug}`} className="group relative flex flex-col h-full rounded-2xl border border-white/[0.07] hover:border-amber-300/40 p-6 transition-colors duration-500 overflow-hidden" data-testid={`document-card-${d.slug}`}>
      {d.hero_image && <><img src={d.hero_image} alt={d.hero_caption || ''} className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-40 transition-opacity duration-700" /><div className="absolute inset-0 bg-gradient-to-t from-[#02040a] via-[#02040a]/80 to-[#02040a]/40" /></>}
      <div className="relative flex flex-col h-full">
      <div className="flex items-center justify-between">
        <span className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500">{KINDS[d.category]?.name || d.category}</span>
        {d.file_type ? <span className="inline-flex items-center gap-1.5 font-mono-precision text-[9px] uppercase tracking-[0.2em] text-slate-400"><FileDown size={11} /> {d.file_type}</span> : <FileText size={13} className="text-slate-600" />}
      </div>
      <h3 className="mt-5 font-celestial text-xl text-slate-100 leading-snug group-hover:text-amber-100 transition-colors flex-1">{d.title}</h3>
      <p className="mt-1.5 font-celestial text-sm text-slate-500"><span className="italic">by</span> {d.author}</p>
      <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-3">{d.summary}</p>
      <div className="mt-5 flex items-center justify-between">
        <Label id={d.label} size="xs" />
        <span className="font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-500 group-hover:text-amber-300 inline-flex items-center gap-1.5 transition-colors">Open <ArrowUpRight size={12} /></span>
      </div>
      </div>
    </Link>
  </Reveal>
);

export default function Documents() {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    document.title = 'Documents — United 99 Unleashed';
    api.get('/documents').then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, []);
  const pubs = docs ? docs.filter((d) => d.body_format === 'text').sort((a, b) => a.publication_order - b.publication_order) : [];
  const rest = docs ? docs.filter((d) => d.body_format !== 'text') : [];
  const groups = docs ? KIND_ORDER.map((k) => ({ kind: k, items: rest.filter((d) => d.category === k) })).filter((g) => g.items.length) : [];

  return (
    <main className="max-w-7xl mx-auto px-5 lg:px-10" data-testid="documents-page">
      <PageHead eyebrow="Documents · Manuscripts, theses, novels, papers" image={HERO_PLASMA} imageAlt="Orange double helix with snowflake blood cells — the internal binary flow of the soul DNA" title={<>Every work, its own <span className="text-amber-300">publication</span></>} lede="Each manuscript, thesis, novel, paper and note is published on its own page under its own title and author — never bundled. Where an original file exists it is offered as a download beneath the text." />
      <section className="py-8 lg:py-12 border-t border-white/[0.07]">
        <BookFeature />
        <div className="mt-4"><Link to="/u99/library" className="inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.25em] text-amber-300 hover:text-amber-200 transition-colors" data-testid="documents-library-link">Full library index · every work, open access →</Link></div>
      </section>
      {pubs.length > 0 && (
        <section className="py-8 lg:py-12 border-t border-white/[0.07]" data-testid="documents-group-publications">
          <Reveal><Eyebrow>Standalone publications · Discovery Attributes, briefs & releases · {pubs.length}</Eyebrow></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pubs.map((d, i) => <DocumentCard key={d.id} d={d} i={i} />)}
          </div>
        </section>
      )}
      {docs === null && <p className="py-10 font-mono-precision text-xs text-slate-600 tracking-[0.2em]">LOADING…</p>}
      {groups.map((g) => (
        <section key={g.kind} className="py-8 lg:py-12 border-t border-white/[0.07]" data-testid={`documents-group-${g.kind}`}>
          <Reveal><Eyebrow>{KINDS[g.kind].plural} · {g.items.length}</Eyebrow></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {g.items.map((d, i) => <DocumentCard key={d.id} d={d} i={i} />)}
          </div>
        </section>
      ))}
      {docs?.length === 0 && <p className="py-10 font-mono-precision text-xs text-slate-600 tracking-[0.2em]">NOTHING PUBLISHED YET</p>}
    </main>
  );
}
